import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { Plus, Key, X, Copy, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  fetchApiKeys,
  createApiKeyApi,
  revokeApiKeyApi,
} from '@/hooks/useAdmin';
import { fetchOperators } from '@/hooks/useTickets';
import type { ApiKeyAdminItem, CreateApiKeyRequest, OperatorListItem } from '@uniticket/shared';

export function ApiKeys() {
  const { t } = useTranslation('admin');
  const [keys, setKeys] = useState<ApiKeyAdminItem[]>([]);
  const [operators, setOperators] = useState<OperatorListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterOperator, setFilterOperator] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [revealedKey, setRevealedKey] = useState<string | null>(null);
  const [revokeConfirm, setRevokeConfirm] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    try {
      const params: Record<string, string> = {};
      if (filterOperator) params.operatorId = filterOperator;
      if (filterStatus) params.active = filterStatus;

      const [keyRes, opRes] = await Promise.all([
        fetchApiKeys(params),
        operators.length > 0 ? Promise.resolve({ data: operators }) : fetchOperators(),
      ]);
      setKeys(keyRes.data);
      if (operators.length === 0) setOperators(opRes.data);
    } catch (err: any) {
      toast.error(err.message || t('apiKeys.loadError'));
    } finally {
      setLoading(false);
    }
  }, [filterOperator, filterStatus, operators.length, t]);

  useEffect(() => { loadData(); }, [loadData]);

  async function handleCreate(data: CreateApiKeyRequest) {
    try {
      const res = await createApiKeyApi(data);
      setRevealedKey(res.data.key);
      setDialogOpen(false);
      loadData();
      toast.success(t('apiKeys.created'));
    } catch (err: any) {
      toast.error(err.message || t('apiKeys.createError'));
    }
  }

  async function handleRevoke(id: string) {
    try {
      await revokeApiKeyApi(id);
      toast.success(t('apiKeys.revoked'));
      setRevokeConfirm(null);
      loadData();
    } catch (err: any) {
      toast.error(err.message || t('apiKeys.revokeError'));
    }
  }

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-10 w-32" />
        </div>
        {[1, 2, 3].map((i) => <Skeleton key={i} className="h-14 w-full" />)}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-brand-gradient flex items-center justify-center">
            <Key className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-slate-800">{t('apiKeys.title')}</h2>
            <p className="text-sm text-slate-500">{t('apiKeys.count', { count: keys.length })}</p>
          </div>
        </div>
        <Button onClick={() => setDialogOpen(true)} className="bg-brand-gradient text-white hover:opacity-90">
          <Plus className="w-4 h-4 mr-2" />
          {t('apiKeys.generate')}
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <Select value={filterOperator} onValueChange={(v) => setFilterOperator(v === 'ALL' ? '' : v)}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder={t('apiKeys.filterOperator')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">{t('apiKeys.allOperators')}</SelectItem>
            {operators.map((op) => <SelectItem key={op.id} value={op.id}>{op.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterStatus} onValueChange={(v) => setFilterStatus(v === 'ALL' ? '' : v)}>
          <SelectTrigger className="w-36">
            <SelectValue placeholder={t('apiKeys.filterStatus')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">{t('apiKeys.allStatuses')}</SelectItem>
            <SelectItem value="true">{t('apiKeys.statusActive')}</SelectItem>
            <SelectItem value="false">{t('apiKeys.statusRevoked')}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-400">{t('apiKeys.cols.prefix')}</th>
                <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-400">{t('apiKeys.cols.label')}</th>
                <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-400">{t('apiKeys.cols.operator')}</th>
                <th className="text-center px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-400">{t('apiKeys.cols.status')}</th>
                <th className="text-right px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-400">{t('apiKeys.cols.rateLimit')}</th>
                <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-400">{t('apiKeys.cols.expires')}</th>
                <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-400">{t('apiKeys.cols.lastUsed')}</th>
                <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-400">{t('apiKeys.cols.createdBy')}</th>
                <th className="text-right px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-400">{t('apiKeys.cols.actions')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {keys.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-4 py-12 text-center text-slate-400">
                    {t('apiKeys.empty')}
                  </td>
                </tr>
              ) : (
                keys.map((k) => (
                  <tr key={k.id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-4 py-3 font-mono text-xs text-slate-600">{k.keyPrefix}...</td>
                    <td className="px-4 py-3 font-medium text-slate-700">
                      <span className="inline-flex items-center gap-2">
                        {k.label}
                        {k.internal && (
                          <span className="rounded-full bg-brand-gradient px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">
                            {t('apiKeys.internalBadge')}
                          </span>
                        )}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-600">{k.operatorName}</td>
                    <td className="px-4 py-3 text-center">
                      {k.isActive ? (
                        <span className="inline-flex items-center text-xs font-semibold text-green-600 bg-green-50 px-2 py-0.5 rounded-full">Active</span>
                      ) : (
                        <span className="inline-flex items-center text-xs font-semibold text-red-500 bg-red-50 px-2 py-0.5 rounded-full">Revoked</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-slate-600">{k.rateLimit}/h</td>
                    <td className="px-4 py-3 text-slate-500 text-xs">
                      {k.expiresAt ? new Date(k.expiresAt).toLocaleDateString() : '—'}
                    </td>
                    <td className="px-4 py-3 text-slate-500 text-xs">
                      {k.lastUsedAt ? new Date(k.lastUsedAt).toLocaleString() : t('apiKeys.never')}
                    </td>
                    <td className="px-4 py-3 text-slate-600 text-xs">{k.createdByName}</td>
                    <td className="px-4 py-3 text-right">
                      {k.isActive && (
                        revokeConfirm === k.id ? (
                          <div className="flex items-center justify-end gap-1">
                            <button
                              onClick={() => handleRevoke(k.id)}
                              className="px-2 py-1 text-xs font-semibold bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
                            >
                              Confirm
                            </button>
                            <button onClick={() => setRevokeConfirm(null)} className="p-1 text-slate-400 hover:text-slate-600">
                              <X className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => setRevokeConfirm(k.id)}
                            className="px-2 py-1 text-xs font-semibold text-red-500 bg-red-50 rounded hover:bg-red-100 transition-colors"
                          >
                            {t('apiKeys.revoke')}
                          </button>
                        )
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create Dialog */}
      {dialogOpen && (
        <CreateApiKeyDialog
          operators={operators}
          onCreate={handleCreate}
          onClose={() => setDialogOpen(false)}
        />
      )}

      {/* One-Time Key Reveal Dialog */}
      {revealedKey && (
        <KeyRevealDialog
          fullKey={revealedKey}
          onClose={() => setRevealedKey(null)}
        />
      )}
    </div>
  );
}

// ── Create API Key Dialog ───────────────────────────────────

interface CreateApiKeyDialogProps {
  operators: OperatorListItem[];
  onCreate: (data: CreateApiKeyRequest) => Promise<void>;
  onClose: () => void;
}

function CreateApiKeyDialog({ operators, onCreate, onClose }: CreateApiKeyDialogProps) {
  const { t } = useTranslation('admin');
  const [operatorId, setOperatorId] = useState('');
  const [label, setLabel] = useState('');
  const [rateLimit, setRateLimit] = useState('100');
  const [expiresAt, setExpiresAt] = useState('');
  const [internal, setInternal] = useState(false);
  const [saving, setSaving] = useState(false);

  // Internal-scope ("master") keys are Unifiber-only. Reset the flag if the operator changes away.
  const selectedOperator = operators.find((o) => o.id === operatorId);
  const isUnf = selectedOperator?.code === 'UNF';

  function handleOperatorChange(value: string) {
    setOperatorId(value);
    const op = operators.find((o) => o.id === value);
    if (op?.code !== 'UNF') setInternal(false);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!operatorId || !label) {
      toast.error(t('apiKeys.operatorLabelRequired'));
      return;
    }
    setSaving(true);
    try {
      await onCreate({
        operatorId,
        label,
        rateLimit: Number(rateLimit) || 100,
        expiresAt: expiresAt ? new Date(expiresAt).toISOString() : undefined,
        internal: internal && isUnf ? true : undefined,
      });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden">
        <div className="gradient-band px-6 py-4 flex items-center justify-between">
          <h3 className="text-sm font-bold text-white uppercase tracking-wide">{t('apiKeys.createTitle')}</h3>
          <button onClick={onClose} className="text-white/60 hover:text-white transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5 max-h-[calc(100vh-8rem)] overflow-y-auto">
          <div className="space-y-1.5">
            <Label>{t('apiKeys.fields.operator')}</Label>
            <Select value={operatorId} onValueChange={handleOperatorChange}>
              <SelectTrigger>
                <SelectValue placeholder={t('apiKeys.fields.selectOperator')} />
              </SelectTrigger>
              <SelectContent>
                {operators.map((op) => <SelectItem key={op.id} value={op.id}>{op.name} ({op.code})</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {isUnf && (
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={internal}
                  onChange={(e) => setInternal(e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-slate-300 accent-[#ed125f]"
                />
                <span>
                  <span className="block text-sm font-medium text-slate-700">{t('apiKeys.fields.internalScope')}</span>
                  <span className="block text-xs text-slate-400">{t('apiKeys.fields.internalScopeHint')}</span>
                </span>
              </label>
            </div>
          )}

          <div className="space-y-1.5">
            <Label>{t('apiKeys.fields.label')}</Label>
            <Input value={label} onChange={(e) => setLabel(e.target.value)} required maxLength={100} placeholder="e.g. Proximus Integration" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>{t('apiKeys.fields.rateLimit')}</Label>
              <Input type="number" min="1" max="10000" value={rateLimit} onChange={(e) => setRateLimit(e.target.value)} />
              <p className="text-xs text-slate-400">{t('apiKeys.fields.rateLimitHint')}</p>
            </div>
            <div className="space-y-1.5">
              <Label>{t('apiKeys.fields.expiresAt')} <span className="text-slate-400 font-normal">({t('apiKeys.optional')})</span></Label>
              <Input type="date" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} />
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="outline" onClick={onClose}>{t('apiKeys.cancel')}</Button>
            <Button type="submit" disabled={saving} className="bg-brand-gradient text-white hover:opacity-90">
              {saving ? t('apiKeys.generating') : t('apiKeys.generateKey')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Key Reveal Dialog (One-Time) ────────────────────────────

interface KeyRevealDialogProps {
  fullKey: string;
  onClose: () => void;
}

function KeyRevealDialog({ fullKey, onClose }: KeyRevealDialogProps) {
  const { t } = useTranslation('admin');
  const [copied, setCopied] = useState(false);

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(fullKey);
      setCopied(true);
      toast.success(t('apiKeys.copied'));
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error(t('apiKeys.copyFailed'));
    }
  }

  async function handleDone() {
    try {
      await navigator.clipboard.writeText(fullKey);
      toast.success(t('apiKeys.copied'));
    } catch {
      // ignore — closing anyway
    }
    onClose();
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden">
        <div className="bg-amber-500 px-6 py-4 flex items-center gap-3">
          <AlertTriangle className="w-5 h-5 text-white" />
          <h3 className="text-sm font-bold text-white uppercase tracking-wide">{t('apiKeys.revealTitle')}</h3>
        </div>

        <div className="p-6 space-y-4 max-h-[calc(100vh-8rem)] overflow-y-auto">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <p className="text-sm font-semibold text-amber-800">{t('apiKeys.revealWarning')}</p>
          </div>

          <div className="bg-slate-900 rounded-lg p-4 flex items-center gap-3">
            <code className="flex-1 text-sm text-green-400 break-all font-mono">{fullKey}</code>
            <button
              onClick={handleCopy}
              className="shrink-0 p-2 rounded-lg bg-slate-700 hover:bg-slate-600 text-white transition-colors"
              title={t('apiKeys.copy')}
            >
              <Copy className="w-4 h-4" />
            </button>
          </div>

          {copied && (
            <p className="text-xs text-green-600 font-semibold text-center">{t('apiKeys.copiedConfirm')}</p>
          )}

          <div className="flex justify-end pt-2">
            <Button onClick={handleDone} className="bg-brand-gradient text-white hover:opacity-90">
              {t('apiKeys.done')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
